// Wedding invitations sent by WhatsApp and email with the family's details. Three steps:
// the wedding details, the guests (from the guest list), then preview & send. Each guest gets
// their own invitation page to reply on; replies update the guest list.
//
// WhatsApp goes out automatically when the admin has connected WhatsApp Business; otherwise each
// guest has a WhatsApp button that opens the family's WhatsApp with the message already written.
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, IconButton, Tooltip, LinearProgress, Paper, Stepper, Step, StepLabel,
  Checkbox, ToggleButton, ToggleButtonGroup, Table, TableHead, TableRow, TableCell, InputAdornment, Snackbar,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import {
  MY_INVITATIONS_QUERY, INVITATION_TEMPLATES_QUERY, MY_CEREMONIES_QUERY, MY_GUESTS_QUERY,
  INVITATION_CHANNELS_QUERY, INVITATION_PREVIEW_QUERY,
  CREATE_INVITATION_MUTATION, UPDATE_INVITATION_MUTATION, DELETE_INVITATION_MUTATION,
  ADD_INVITATION_RECIPIENT_MUTATION, ADD_INVITATION_GUESTS_MUTATION, DELETE_INVITATION_RECIPIENT_MUTATION,
  SEND_INVITATION_MUTATION, MARK_INVITATION_WHATSAPP_SENT_MUTATION,
} from '../graphql/queries';
import Paged from '../components/Paged'; // lists beyond 10 are paged

const STEPS = ['Wedding details', 'Guests', 'Preview & send'];
const VIA = [
  { value: 'both', label: 'WhatsApp & Email', icon: <><WhatsAppIcon fontSize="small" /><EmailIcon fontSize="small" /></> },
  { value: 'whatsapp', label: 'WhatsApp', icon: <WhatsAppIcon fontSize="small" /> },
  { value: 'email', label: 'Email', icon: <EmailIcon fontSize="small" /> },
];
const RSVP = {
  attending: { label: 'Attending', color: 'success' }, not_attending: { label: 'Not attending', color: 'error' },
  maybe: { label: 'Maybe', color: 'warning' }, pending: { label: 'No reply yet', color: 'default' },
};
const EMPTY_DETAILS = {
  brideName: '', groomName: '', eventDate: '', eventTime: '', venue: '', address: '', mapLink: '',
  hosts: '', contactPhone: '', dressCode: '', rsvpBy: '',
};
const EMPTY_FORM = { title: 'Wedding Invitation', ceremonyId: '', templateId: '', customMessage: '', dispatchMethod: 'both', details: EMPTY_DETAILS };
const fmtDate = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '');
const coupleOf = (d) => [d?.brideName, d?.groomName].filter(Boolean).join(' & ');

// ── Channel status chips for one guest ───────────────────────────────
function ChannelChips({ r }) {
  const wa = {
    sent: ['WhatsApp sent', 'success'], manual_sent: ['WhatsApp sent', 'success'], manual: ['WhatsApp: send from your phone', 'warning'],
    failed: ['WhatsApp failed', 'error'], skipped: ['No WhatsApp number', 'default'],
  }[r.whatsappStatus];
  const em = { sent: ['Email sent', 'success'], failed: ['Email failed', 'error'], skipped: ['No email', 'default'] }[r.emailStatus];
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {wa && <Chip size="small" variant="outlined" color={wa[1]} icon={<WhatsAppIcon />} label={wa[0]} />}
      {em && <Chip size="small" variant="outlined" color={em[1]} icon={<EmailIcon />} label={em[0]} />}
      {!wa && !em && <Chip size="small" variant="outlined" label="Not sent yet" />}
      {r.openedAt && <Chip size="small" variant="outlined" color="info" icon={<VisibilityIcon />} label="Opened" />}
    </Stack>
  );
}

// ── Preview of what a guest receives ─────────────────────────────────
function Preview({ invitationId }) {
  const { data, loading } = useQuery(INVITATION_PREVIEW_QUERY, { variables: { invitationId }, fetchPolicy: 'network-only' });
  const [tab, setTab] = useState('whatsapp');
  const p = data?.invitationPreview;
  if (loading || !p) return <LinearProgress />;
  return (
    <Box>
      <ToggleButtonGroup exclusive size="small" value={tab} onChange={(_, v) => v && setTab(v)} sx={{ mb: 1.5 }}>
        <ToggleButton value="whatsapp"><WhatsAppIcon fontSize="small" sx={{ mr: 0.5 }} />WhatsApp</ToggleButton>
        <ToggleButton value="email"><EmailIcon fontSize="small" sx={{ mr: 0.5 }} />Email</ToggleButton>
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>As {p.guestName} will receive it (each guest gets their own name and link)</Typography>
      {tab === 'whatsapp' ? (
        <Box sx={{ bgcolor: '#e5ddd5', p: 2, borderRadius: 2 }}>
          <Paper sx={{ p: 1.5, maxWidth: 420, ml: 'auto', bgcolor: '#dcf8c6', borderRadius: 2, whiteSpace: 'pre-wrap', fontSize: 14 }}>
            {p.whatsappText.split(/(\*[^*]+\*)/).map((part, i) => (part.startsWith('*') && part.endsWith('*') ? <b key={i}>{part.slice(1, -1)}</b> : part))}
          </Paper>
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" mb={1}><b>Subject:</b> {p.emailSubject}</Typography>
          <Box component="iframe" title="Email preview" srcDoc={p.emailHtml} sandbox="" sx={{ width: '100%', height: 460, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fff' }} />
        </Box>
      )}
    </Box>
  );
}

// ── Step 2: choose guests from the guest list ────────────────────────
function GuestPicker({ guests, invited, selected, onChange }) {
  const [search, setSearch] = useState('');
  const [side, setSide] = useState('all');
  const shown = guests.filter(g => (side === 'all' || g.side === side) && (!search || g.name.toLowerCase().includes(search.toLowerCase())));
  const selectable = shown.filter(g => !invited.has(g.id));
  const allOn = selectable.length > 0 && selectable.every(g => selected.has(g.id));
  const toggle = (id) => { const next = new Set(selected); if (next.has(id)) next.delete(id); else next.add(id); onChange(next); };
  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mb={1}>
        <TextField size="small" placeholder="Search guests" value={search} onChange={e => setSearch(e.target.value)} sx={{ flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" value={side} onChange={e => setSide(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="all">Everyone</MenuItem>
          <MenuItem value="bride">Bride's side</MenuItem>
          <MenuItem value="groom">Groom's side</MenuItem>
          <MenuItem value="neutral">Common</MenuItem>
        </TextField>
      </Stack>
      {guests.length === 0 ? (
        <Alert severity="info">Your guest list is empty. Add guests in Guest List, or add a guest by hand below.</Alert>
      ) : (
        <Paper variant="outlined" sx={{ maxHeight: 340, overflowY: 'auto' }}>
          <Stack direction="row" alignItems="center" sx={{ px: 1, py: 0.5, borderBottom: '1px solid #eee', bgcolor: '#fafafa' }}>
            <Checkbox size="small" checked={allOn} indeterminate={!allOn && selectable.some(g => selected.has(g.id))}
              onChange={() => { const next = new Set(selected); selectable.forEach(g => (allOn ? next.delete(g.id) : next.add(g.id))); onChange(next); }} />
            <Typography variant="body2" fontWeight={600}>Select all shown ({selectable.length})</Typography>
            <Typography variant="body2" color="text.secondary" ml="auto">{selected.size} selected</Typography>
          </Stack>
          {shown.map(g => {
            const already = invited.has(g.id);
            return (
              <Stack key={g.id} direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.25, opacity: already ? 0.6 : 1 }}>
                <Checkbox size="small" checked={already || selected.has(g.id)} disabled={already} onChange={() => toggle(g.id)} />
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight={500} noWrap>{g.name}{g.relation ? ` · ${g.relation}` : ''}</Typography>
                  <Typography variant="caption" color="text.secondary">{[g.phone, g.email].filter(Boolean).join(' · ') || 'No phone or email'}</Typography>
                </Box>
                {already && <Chip size="small" label="Invited" />}
                <Tooltip title={g.phone ? 'Has a WhatsApp number' : 'No phone number'}><WhatsAppIcon fontSize="small" color={g.phone ? 'success' : 'disabled'} /></Tooltip>
                <Tooltip title={g.email ? 'Has an email' : 'No email'}><EmailIcon fontSize="small" color={g.email ? 'primary' : 'disabled'} /></Tooltip>
              </Stack>
            );
          })}
        </Paper>
      )}
    </Box>
  );
}

export default function InvitationPage() {
  const [activeId, setActiveId] = useState('');
  const [wizard, setWizard] = useState(null); // { step, editing (invitation|null), form, selected:Set, extra:[], savedId }
  const [previewId, setPreviewId] = useState(null);
  const [addOne, setAddOne] = useState(null); // { name, phone, email }
  const [pickFor, setPickFor] = useState(null); // invitation to add guests to
  const [pickSel, setPickSel] = useState(new Set());
  const [confirmDel, setConfirmDel] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  // Sending: the family confirms the guests are happy to receive it
  const [consentFor, setConsentFor] = useState(null); // { id, extra, afterSend }
  const [consentOk, setConsentOk] = useState(false);

  const { data: invData, loading, refetch } = useQuery(MY_INVITATIONS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: tplData } = useQuery(INVITATION_TEMPLATES_QUERY);
  const { data: cerData } = useQuery(MY_CEREMONIES_QUERY);
  const { data: guestData } = useQuery(MY_GUESTS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: chData } = useQuery(INVITATION_CHANNELS_QUERY);

  const invitations = invData?.myInvitations || [];
  const templates = tplData?.invitationTemplates || [];
  const ceremonies = cerData?.myCeremonies || [];
  const guests = guestData?.myGuests || [];
  const channels = chData?.invitationChannels;
  const active = invitations.find(i => i.id === activeId) || null;

  const onError = (e) => setError(e.message);
  const [createInv] = useMutation(CREATE_INVITATION_MUTATION, { onError });
  const [updateInv] = useMutation(UPDATE_INVITATION_MUTATION, { onError });
  const [deleteInv] = useMutation(DELETE_INVITATION_MUTATION, { onCompleted: () => { setConfirmDel(null); setActiveId(''); refetch(); } });
  const [addRcp] = useMutation(ADD_INVITATION_RECIPIENT_MUTATION, { onError });
  const [addGuests] = useMutation(ADD_INVITATION_GUESTS_MUTATION, { onError });
  const [deleteRcp] = useMutation(DELETE_INVITATION_RECIPIENT_MUTATION, { onCompleted: () => refetch() });
  const [sendInv, { loading: sending }] = useMutation(SEND_INVITATION_MUTATION, { onError });
  const [markSent] = useMutation(MARK_INVITATION_WHATSAPP_SENT_MUTATION, { onCompleted: () => refetch() });

  const stats = useMemo(() => {
    const rs = active?.recipients || [];
    const people = (k) => rs.filter(r => r.rsvpResponse === k).reduce((n, r) => n + (r.guestsCount || 1), 0);
    return {
      total: rs.length,
      sent: rs.filter(r => r.sentAt).length,
      opened: rs.filter(r => r.openedAt).length,
      attending: rs.filter(r => r.rsvpResponse === 'attending').length,
      attendingPeople: people('attending'),
      maybe: rs.filter(r => r.rsvpResponse === 'maybe').length,
      declined: rs.filter(r => r.rsvpResponse === 'not_attending').length,
      noReply: rs.filter(r => !r.rsvpResponse || r.rsvpResponse === 'pending').length,
      unsent: rs.filter(r => !r.sentAt && r.whatsappStatus !== 'manual_sent').length,
      manual: rs.filter(r => r.whatsappStatus === 'manual'),
    };
  }, [active]);

  // ── Wizard ─────────────────────────────────────────────────────────
  const openNew = () => setWizard({ step: 0, editing: null, form: EMPTY_FORM, selected: new Set(), extra: [], savedId: null });
  const openEdit = (inv) => setWizard({
    step: 0, editing: inv, selected: new Set(), extra: [], savedId: inv.id,
    form: {
      title: inv.title || '', ceremonyId: inv.ceremonyId || '', templateId: inv.templateId || '', customMessage: inv.customMessage || '',
      dispatchMethod: ['both', 'whatsapp', 'email'].includes(inv.dispatchMethod) ? inv.dispatchMethod : 'both',
      details: { ...EMPTY_DETAILS, ...(inv.details || {}) },
    },
  });
  const setForm = (patch) => setWizard(w => ({ ...w, form: { ...w.form, ...patch } }));
  const setDetail = (k) => (e) => setWizard(w => ({ ...w, form: { ...w.form, details: { ...w.form.details, [k]: e.target.value } } }));
  const pickCeremony = (id) => {
    const c = ceremonies.find(x => x.id === id);
    setWizard(w => ({
      ...w,
      form: {
        ...w.form, ceremonyId: id,
        details: { ...w.form.details, eventDate: w.form.details.eventDate || c?.eventDate || '', venue: w.form.details.venue || c?.venue || '' },
      },
    }));
  };

  // Saves the invitation and adds the chosen guests, then shows the preview
  const saveDraft = async () => {
    setError('');
    const f = wizard.form;
    const vars = {
      title: f.title.trim() || 'Wedding Invitation', ceremonyId: f.ceremonyId || null, templateId: f.templateId || null,
      customMessage: f.customMessage || null, dispatchMethod: f.dispatchMethod,
      details: Object.fromEntries(Object.entries(f.details).filter(([, v]) => v)),
    };
    let id = wizard.savedId;
    const res = id ? await updateInv({ variables: { id, ...vars } }) : await createInv({ variables: vars });
    if (!res?.data) return;
    id = id || res.data.createInvitation.id;
    if (wizard.selected.size) await addGuests({ variables: { invitationId: id, guestIds: [...wizard.selected], dispatchMethod: f.dispatchMethod } });
    for (const g of wizard.extra) await addRcp({ variables: { invitationId: id, guestName: g.name, phone: g.phone || null, email: g.email || null, dispatchMethod: f.dispatchMethod } });
    await refetch();
    setActiveId(id);
    setWizard(w => ({ ...w, savedId: id, step: 2, selected: new Set(), extra: [] }));
  };

  // Asks for the guests' consent, then sends
  const askSend = (id, extra = {}, afterSend) => { setConsentOk(false); setConsentFor({ id, extra, afterSend }); };
  const doSend = async (id, extra = {}) => {
    setError('');
    const res = await sendInv({ variables: { id, ...extra, guestConsent: true } });
    if (!res?.data) return;
    const s = res.data.sendInvitation;
    const parts = [
      s.emailed && `${s.emailed} email(s) sent`,
      s.whatsappSent && `${s.whatsappSent} WhatsApp message(s) sent`,
      s.whatsappManual && `${s.whatsappManual} WhatsApp message(s) ready to send from your phone — use the green WhatsApp buttons`,
      (s.emailFailed || s.whatsappFailed) && `${s.emailFailed + s.whatsappFailed} could not be sent (see the guest list)`,
      s.skipped && `${s.skipped} skipped (no phone number / email)`,
    ].filter(Boolean);
    setToast(parts.join(' · ') || 'Done');
    await refetch();
  };

  const openWhatsApp = (r) => {
    window.open(r.whatsappLink, '_blank', 'noopener');
    markSent({ variables: { recipientId: r.id } });
  };
  const copyLink = async (r) => { try { await navigator.clipboard.writeText(r.inviteLink); setToast(`Invitation link for ${r.guestName} copied`); } catch (e) { setToast(r.inviteLink); } };

  const invitedIds = new Set((invitations.find(i => i.id === (wizard?.savedId || pickFor?.id))?.recipients || []).map(r => r.guestId).filter(Boolean));
  const d = wizard?.form.details;
  const step1Ready = d && (d.brideName || d.groomName) && d.eventDate && d.venue;

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Invitations</Typography>
          <Typography variant="body2" color="text.secondary">
            Send your wedding invitation by WhatsApp and email. Every guest gets a personal invitation page to reply on.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>New invitation</Button>
      </Stack>

      {channels && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} mb={2}>
          <Alert severity={channels.whatsappAutomatic ? 'success' : 'info'} icon={<WhatsAppIcon />} sx={{ flex: 1 }}>
            {channels.whatsappAutomatic
              ? `WhatsApp invitations are sent automatically${channels.whatsappBusinessNumber ? ` from ${channels.whatsappBusinessNumber}` : ''}.`
              : 'WhatsApp invitations are sent from your own WhatsApp: click the green button next to each guest — the message is already written, just press Send.'}
          </Alert>
          <Alert severity={channels.emailReady ? 'success' : 'warning'} icon={<EmailIcon />} sx={{ flex: 1 }}>
            {channels.emailReady ? 'Email invitations are sent automatically.' : 'Email is not set up yet (ask the admin: Admin → Email Settings). Use WhatsApp, or copy each guest\'s invitation link.'}
          </Alert>
        </Stack>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        {/* Invitations */}
        <Grid item xs={12} md={4}>
          {invitations.length === 0 && !loading && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <MarkEmailReadIcon sx={{ fontSize: 48, color: 'primary.light' }} />
              <Typography fontWeight={600} mt={1}>No invitations yet</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>Enter your wedding details, pick your guests and send — in three steps.</Typography>
              <Button variant="contained" onClick={openNew}>Create your invitation</Button>
            </Paper>
          )}
          <Paged items={invitations}>{rows => rows.map(inv => {
            const rs = inv.recipients || [];
            return (
              <Card key={inv.id} variant="outlined" onClick={() => setActiveId(inv.id)}
                sx={{ mb: 1.5, cursor: 'pointer', borderColor: activeId === inv.id ? 'primary.main' : 'divider', borderWidth: activeId === inv.id ? 2 : 1 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography fontWeight={700} noWrap>{coupleOf(inv.details) || inv.title}</Typography>
                    <Chip size="small" label={inv.status === 'sent' ? 'Sent' : 'Draft'} color={inv.status === 'sent' ? 'success' : 'default'} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {[inv.title, fmtDate(inv.details?.eventDate)].filter(Boolean).join(' · ')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {rs.length} guests · {rs.filter(r => r.sentAt).length} sent · {rs.filter(r => r.rsvpResponse === 'attending').length} attending
                  </Typography>
                </CardContent>
              </Card>
            );
          })}</Paged>
        </Grid>

        {/* Selected invitation */}
        <Grid item xs={12} md={8}>
          {!active ? (
            invitations.length > 0 && (
              <Box sx={{ textAlign: 'center', mt: 8, color: 'text.secondary' }}>
                <MarkEmailReadIcon sx={{ fontSize: 64, opacity: 0.2 }} />
                <Typography mt={1}>Select an invitation to see its guests and replies</Typography>
              </Box>
            )
          ) : (
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} mb={2}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{coupleOf(active.details) || active.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[fmtDate(active.details?.eventDate), active.details?.eventTime, active.details?.venue].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Preview"><IconButton onClick={() => setPreviewId(active.id)}><VisibilityIcon /></IconButton></Tooltip>
                  <Tooltip title="Edit details"><IconButton onClick={() => openEdit(active)}><EditIcon /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton color="error" onClick={() => setConfirmDel(active.id)}><DeleteIcon /></IconButton></Tooltip>
                </Stack>
              </Stack>

              <Grid container spacing={1} mb={2}>
                {[
                  ['Guests', stats.total], ['Sent', stats.sent], ['Opened', stats.opened],
                  ['Attending', stats.attending ? `${stats.attending} (${stats.attendingPeople} people)` : 0],
                  ['Maybe', stats.maybe], ['Not attending', stats.declined], ['No reply yet', stats.noReply],
                ].map(([label, val]) => (
                  <Grid item xs={6} sm={3} md key={label}>
                    <Box textAlign="center" sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
                      <Typography variant="h6" fontWeight={700}>{val}</Typography>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
                <Button variant="contained" startIcon={<SendIcon />} disabled={!stats.unsent || sending} onClick={() => askSend(active.id)}>
                  {sending ? 'Sending…' : stats.unsent ? `Send to ${stats.unsent} guest${stats.unsent === 1 ? '' : 's'}` : 'Everyone has been sent it'}
                </Button>
                <Button variant="outlined" startIcon={<GroupAddIcon />} onClick={() => { setPickFor(active); setPickSel(new Set()); }}>Add from guest list</Button>
                <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => setAddOne({ name: '', phone: '', email: '' })}>Add a guest</Button>
              </Stack>

              {stats.manual.length > 0 && (
                <Alert severity="warning" icon={<WhatsAppIcon />} sx={{ mb: 2 }}>
                  {stats.manual.length} WhatsApp invitation{stats.manual.length === 1 ? '' : 's'} to send from your phone. Click the green
                  <WhatsAppIcon fontSize="inherit" sx={{ mx: 0.5, verticalAlign: 'middle', color: '#25D366' }} />
                  button for each guest: WhatsApp opens with the message written — press Send.
                </Alert>
              )}

              {(active.recipients || []).length === 0 ? (
                <Alert severity="info">No guests on this invitation yet — add them from your guest list.</Alert>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell>Guest</TableCell><TableCell>Sent by</TableCell><TableCell>Reply</TableCell><TableCell align="right">Actions</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {(active.recipients || []).map(r => {
                        const rsvp = RSVP[r.rsvpResponse] || RSVP.pending;
                        return (
                          <TableRow key={r.id}>
                            <TableCell sx={{ minWidth: 160 }}>
                              <Typography variant="body2" fontWeight={600}>{r.guestName}</Typography>
                              <Typography variant="caption" color="text.secondary">{[r.phone, r.email].filter(Boolean).join(' · ') || 'No phone or email'}</Typography>
                              {r.lastError && <Typography variant="caption" color="error" display="block">{r.lastError}</Typography>}
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}><ChannelChips r={r} /></TableCell>
                            <TableCell sx={{ minWidth: 130 }}>
                              <Chip size="small" color={rsvp.color} label={rsvp.label + (r.rsvpResponse === 'attending' && r.guestsCount > 1 ? ` · ${r.guestsCount}` : '')} />
                              {r.rsvpNote && <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>"{r.rsvpNote}"</Typography>}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {r.whatsappLink && !channels?.whatsappAutomatic && (
                                <Tooltip title={r.whatsappStatus === 'manual_sent' ? 'Open WhatsApp again' : 'Send on WhatsApp (opens with the message written)'}>
                                  <IconButton size="small" sx={{ color: '#25D366' }} onClick={() => openWhatsApp(r)}><WhatsAppIcon /></IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Copy their invitation link"><IconButton size="small" onClick={() => copyLink(r)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Send again"><IconButton size="small" onClick={() => askSend(active.id, { recipientIds: [r.id] })}><ReplayIcon fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => deleteRcp({ variables: { id: r.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* ── New / edit: 3 steps ── */}
      {wizard && (
        <Dialog open onClose={() => setWizard(null)} maxWidth="md" fullWidth>
          <DialogTitle>{wizard.editing ? 'Edit invitation' : 'New invitation'}</DialogTitle>
          <DialogContent>
            <Stepper activeStep={wizard.step} alternativeLabel sx={{ mb: 3, mt: 1 }}>
              {STEPS.map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
            </Stepper>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {wizard.step === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField label="Bride's name" value={d.brideName} onChange={setDetail('brideName')} fullWidth required /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Groom's name" value={d.groomName} onChange={setDetail('groomName')} fullWidth required /></Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select label="Ceremony (optional)" value={wizard.form.ceremonyId} onChange={e => pickCeremony(e.target.value)} fullWidth
                    helperText="Fills in the date and venue from your rituals">
                    <MenuItem value="">— None —</MenuItem>
                    {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.eventDate ? ` · ${fmtDate(c.eventDate)}` : ''}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}><TextField label="Invitation title" value={wizard.form.title} onChange={e => setForm({ title: e.target.value })} fullWidth helperText="For your own reference" /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Date" type="date" value={d.eventDate} onChange={setDetail('eventDate')} fullWidth required InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Time" value={d.eventTime} onChange={setDetail('eventTime')} fullWidth placeholder="e.g. Muhurtham 9:00 – 10:30 am" /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Venue" value={d.venue} onChange={setDetail('venue')} fullWidth required placeholder="e.g. Sri Kalyana Mandapam" /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Address" value={d.address} onChange={setDetail('address')} fullWidth placeholder="e.g. 12 North Usman Road, T. Nagar, Chennai" /></Grid>
                <Grid item xs={12}><TextField label="Google Maps link" value={d.mapLink} onChange={setDetail('mapLink')} fullWidth
                  helperText="In Google Maps: open the venue → Share → Copy link" /></Grid>
                <Grid item xs={12}><TextField label="Message to your guests (optional)" value={wizard.form.customMessage} onChange={e => setForm({ customMessage: e.target.value })} fullWidth multiline minRows={2}
                  placeholder="e.g. Lunch will be served after the Muhurtham. Reception at 7 pm." /></Grid>
                <Grid item xs={12} sm={6}><TextField label="With love from" value={d.hosts} onChange={setDetail('hosts')} fullWidth placeholder="e.g. Mr & Mrs Sundaram and family" /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Contact number for guests" value={d.contactPhone} onChange={setDetail('contactPhone')} fullWidth /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Dress code (optional)" value={d.dressCode} onChange={setDetail('dressCode')} fullWidth placeholder="e.g. Traditional" /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Reply by (optional)" type="date" value={d.rsvpBy} onChange={setDetail('rsvpBy')} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
                {templates.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <TextField select label="Email design (optional)" value={wizard.form.templateId} onChange={e => setForm({ templateId: e.target.value })} fullWidth>
                      <MenuItem value="">Classic (default)</MenuItem>
                      {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                    </TextField>
                  </Grid>
                )}
              </Grid>
            )}

            {wizard.step === 1 && (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>Send by</Typography>
                  <ToggleButtonGroup exclusive color="primary" value={wizard.form.dispatchMethod} onChange={(_, v) => v && setForm({ dispatchMethod: v })}>
                    {VIA.map(v => <ToggleButton key={v.value} value={v.value} sx={{ gap: 0.5 }}>{v.icon}{v.label}</ToggleButton>)}
                  </ToggleButtonGroup>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>Choose guests from your guest list</Typography>
                  <GuestPicker guests={guests} invited={invitedIds} selected={wizard.selected} onChange={(sel) => setWizard(w => ({ ...w, selected: sel }))} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>Or add someone not on the guest list</Typography>
                  {wizard.extra.map((g, i) => (
                    <Chip key={i} label={`${g.name}${g.phone ? ` · ${g.phone}` : ''}`} sx={{ mr: 0.5, mb: 0.5 }}
                      onDelete={() => setWizard(w => ({ ...w, extra: w.extra.filter((_, k) => k !== i) }))} />
                  ))}
                  <QuickAdd onAdd={(g) => setWizard(w => ({ ...w, extra: [...w.extra, g] }))} />
                </Box>
              </Stack>
            )}

            {wizard.step === 2 && wizard.savedId && <Preview invitationId={wizard.savedId} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWizard(null)}>Close</Button>
            {wizard.step > 0 && wizard.step < 2 && <Button onClick={() => setWizard(w => ({ ...w, step: w.step - 1 }))}>Back</Button>}
            {wizard.step === 2 && <Button onClick={() => setWizard(w => ({ ...w, step: 1 }))}>Back to guests</Button>}
            {wizard.step === 0 && <Button variant="contained" disabled={!step1Ready} onClick={() => setWizard(w => ({ ...w, step: 1 }))}>Next: Guests</Button>}
            {wizard.step === 1 && <Button variant="contained" onClick={saveDraft}>Save & preview</Button>}
            {wizard.step === 2 && (
              <>
                <Button onClick={() => setWizard(null)}>Save as draft</Button>
                <Button variant="contained" startIcon={<SendIcon />} disabled={sending}
                  onClick={() => askSend(wizard.savedId, {}, () => setWizard(null))}>
                  {sending ? 'Sending…' : 'Send now'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* Preview */}
      <Dialog open={Boolean(previewId)} onClose={() => setPreviewId(null)} maxWidth="md" fullWidth>
        <DialogTitle>Preview</DialogTitle>
        <DialogContent>{previewId && <Preview invitationId={previewId} />}</DialogContent>
        <DialogActions><Button onClick={() => setPreviewId(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Add from guest list */}
      <Dialog open={Boolean(pickFor)} onClose={() => setPickFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add guests from your guest list</DialogTitle>
        <DialogContent>
          {pickFor && <GuestPicker guests={guests} invited={invitedIds} selected={pickSel} onChange={setPickSel} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickFor(null)}>Cancel</Button>
          <Button variant="contained" disabled={!pickSel.size}
            onClick={async () => { await addGuests({ variables: { invitationId: pickFor.id, guestIds: [...pickSel] } }); setPickFor(null); refetch(); }}>
            Add {pickSel.size || ''} guest{pickSel.size === 1 ? '' : 's'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add one guest */}
      <Dialog open={Boolean(addOne)} onClose={() => setAddOne(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add a guest</DialogTitle>
        <DialogContent>
          {addOne && (
            <Stack spacing={2} mt={1}>
              <TextField label="Name *" value={addOne.name} onChange={e => setAddOne(a => ({ ...a, name: e.target.value }))} fullWidth />
              <TextField label="WhatsApp number" value={addOne.phone} onChange={e => setAddOne(a => ({ ...a, phone: e.target.value }))} fullWidth helperText="With country code for numbers outside India, e.g. +971 50 123 4567" />
              <TextField label="Email" value={addOne.email} onChange={e => setAddOne(a => ({ ...a, email: e.target.value }))} fullWidth />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOne(null)}>Cancel</Button>
          <Button variant="contained" disabled={!addOne?.name.trim()}
            onClick={async () => { await addRcp({ variables: { invitationId: active.id, guestName: addOne.name.trim(), phone: addOne.phone || null, email: addOne.email || null, dispatchMethod: active.dispatchMethod } }); setAddOne(null); refetch(); }}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog open={Boolean(confirmDel)} onClose={() => setConfirmDel(null)}>
        <DialogTitle>Delete this invitation?</DialogTitle>
        <DialogContent><Typography>Its guest list and replies will be removed. Guests' invitation links stop working.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => deleteInv({ variables: { id: confirmDel } })}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Guests' consent (WhatsApp policy and data protection) */}
      <Dialog open={Boolean(consentFor)} onClose={() => setConsentFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Before sending</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1}>
            Invitations go to your guests' WhatsApp and email. Each message includes a link guests can use to reply or to stop further messages.
          </Typography>
          <FormControlLabelCheckbox checked={consentOk} onChange={setConsentOk}
            label="I confirm these are people I know personally, I may share their contact details for this invitation, and they are happy to receive it." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsentFor(null)}>Cancel</Button>
          <Button variant="contained" startIcon={<SendIcon />} disabled={!consentOk || sending}
            onClick={async () => { const c = consentFor; setConsentFor(null); await doSend(c.id, c.extra); c.afterSend?.(); }}>
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={7000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}

function FormControlLabelCheckbox({ checked, onChange, label }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={0.5}>
      <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} sx={{ pt: 0.25 }} />
      <Typography variant="body2" sx={{ pt: 0.75 }}>{label}</Typography>
    </Stack>
  );
}

// A guest who isn't on the guest list (name + WhatsApp number / email)
function QuickAdd({ onAdd }) {
  const [g, setG] = useState({ name: '', phone: '', email: '' });
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={1}>
      <TextField size="small" label="Name" value={g.name} onChange={e => setG(x => ({ ...x, name: e.target.value }))} />
      <TextField size="small" label="WhatsApp number" value={g.phone} onChange={e => setG(x => ({ ...x, phone: e.target.value }))} />
      <TextField size="small" label="Email" value={g.email} onChange={e => setG(x => ({ ...x, email: e.target.value }))} />
      <Button size="small" variant="outlined" disabled={!g.name.trim() || (!g.phone.trim() && !g.email.trim())}
        onClick={() => { onAdd({ name: g.name.trim(), phone: g.phone.trim(), email: g.email.trim() }); setG({ name: '', phone: '', email: '' }); }}>
        Add
      </Button>
    </Stack>
  );
}
